import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Paperclip, X } from "lucide-react";
import { api, errorText } from "@/lib/api";

const ACCEPT = ".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png";
const MAX = 15 * 1024 * 1024;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const AttachmentField = ({ value, onChange }) => {
  const input = useRef(null);
  const [progress, setProgress] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX) { toast.error("File must be under 15 MB"); return; }
    const body = new FormData();
    body.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post("/contact/attachment", body, {
        onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0),
      });
      onChange(data);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setProgress(null);
    }
  };

  if (value) {
    return (
      <div data-testid="contact-attachment-chip" className="flex items-center gap-3 border border-[var(--gold)]/60 bg-white/60 px-4 py-3">
        <FileText size={16} className="text-[var(--gold)] shrink-0" />
        <span className="text-sm text-stone-800 truncate">{value.filename}</span>
        <span className="text-[12px] text-stone-500 shrink-0">{kb(value.size)}</span>
        <button type="button" data-testid="contact-attachment-remove" aria-label="Remove attachment" onClick={() => onChange(null)}
          className="ml-auto p-1 text-stone-500 hover:text-[var(--ink)]"><X size={16} /></button>
      </div>
    );
  }
  return (
    <div>
      <input ref={input} type="file" accept={ACCEPT} className="hidden" data-testid="contact-attachment-input" onChange={pick} />
      <button type="button" data-testid="contact-attachment-btn" disabled={progress !== null} onClick={() => input.current?.click()}
        className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-stone-600 hover:text-[var(--gold)] transition-colors duration-300 disabled:opacity-70">
        {progress !== null ? <Loader2 size={14} className="animate-spin text-[var(--gold)]" /> : <Paperclip size={14} className="text-[var(--gold)]" />}
        {progress !== null ? `Uploading… ${progress}%` : "Attach sides or script"}
        <span className="normal-case tracking-normal text-stone-500">· optional, up to 15 MB</span>
      </button>
    </div>
  );
};
